import {
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  Box,
} from "@mui/material";
import React, { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { BodyText } from "../../../widgets/TemplatePreview";

/**
 * ShowEndpointMessage
 * -------------------
 * A compact card that displays an endpoint URL, its trigger keywords,
 * and an intuitive usage guide with a ready-to-copy cURL example.
 *
 * Props
 * -----
 * data: {
 *   url: string,
 *   triggerText: string[]
 * }
 * onOpen: () => void
 */
const ShowEndpointMessage = ({ data, onOpen, preview = false }) => {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);

  const curlExample = `curl -X POST "${data.url}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phoneNumber": "<YOUR_PHONE_NUMBER_WITH_COUNTRY_CODE>",
    "profileName": "<YOUR_PROFILE_NAME>",
    "keyword": "<YOUR_KEYWORD>"
  }'`;

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(data.url).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };

  const handleCopyCurl = () => {
    navigator.clipboard.writeText(curlExample).then(() => {
      setCopiedCurl(true);
      setTimeout(() => setCopiedCurl(false), 2000);
    });
  };

  return (
    <Box maxWidth={420}>
      <Paper sx={{ p: 2, maxWidth: "sm" }} variant="outlined" elevation={0}>
        {/* Trigger keywords */}
        {!preview && (
          <Stack my={1} direction="row" spacing={1} flexWrap="wrap">
            {data.triggerText.map((item) => (
              <Chip key={item} variant="outlined" label={item} clickable />
            ))}
          </Stack>
        )}

        {/* Endpoint URL with copy */}
        <Stack my={2} direction="row" alignItems="center" spacing={1}>
          <Typography
            color="darkblue"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "calc(100% - 40px)",
              fontWeight: 500,
            }}
          >
            {data.url}
          </Typography>
          <Tooltip title={copiedUrl ? "Copied!" : "Copy URL"} arrow>
            <IconButton onClick={handleCopyUrl} size="small">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Usage guide */}
        <Stack spacing={1} my={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            API Usage Guide
          </Typography>
          <BodyText>
            <strong>Method:</strong> POST
          </BodyText>
          <BodyText>
            <strong>Parameters:</strong>
          </BodyText>
          <ul style={{ marginTop: 0, paddingLeft: 20 }}>
            <li>
              <code>phoneNumber</code> <em>(string)</em> – Phone number
              including the country code (e.g., <code>919876543210</code>).
            </li>
            <li>
              <code>profileName</code> <em>(string)</em> – Display name of the
              WhatsApp profile.
            </li>
            <li>
              <code>keyword</code> <em>(string)</em> – Trigger keyword linked to
              this endpoint.
            </li>
          </ul>

          {/* cURL example */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={500}>
              Example&nbsp;(cURL):
            </Typography>
            <Tooltip title={copiedCurl ? "Copied!" : "Copy snippet"} arrow>
              <IconButton onClick={handleCopyCurl} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <SyntaxHighlighter
            language="bash"
            style={materialDark}
            customStyle={{ borderRadius: 8, fontSize: 14 }}
          >
            {curlExample}
          </SyntaxHighlighter>
        </Stack>

        {/* Action button */}
        {!preview && (
          <Stack direction="row" justifyContent="end">
            <Button
              onClick={() => onOpen?.()}
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
            >
              Add trigger
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default ShowEndpointMessage;
